import {
  ContractTemplateVariables,
  QuoteTemplateVariables,
  TemplatePlaceholderMetadata,
} from "../interfaces/custom-template.interface";

/**
 * Mock data used to validate a custom quote template at upload time.
 * Covers the realistic edge cases (non-empty loops, present discount,
 * inarcassa, all currency formatting). All keys are Italian snake_case
 * to match the user-facing placeholder schema.
 */
export const QUOTE_TEMPLATE_MOCK_DATA: QuoteTemplateVariables = {
  codice_preventivo: "PRV-2024-001",
  nome_preventivo: "Progettazione studio grafico",
  data_generazione: "15/04/2026",

  nome_cliente: "Mario Rossi S.r.l.",
  piva_cliente: "IT12345678901",

  nome_azienda: "Acme Corporation S.r.l.",
  indirizzo_azienda: "Via Roma 123",
  citta_azienda: "Milano",
  provincia_azienda: "MI",
  cap_azienda: "20100",
  piva_azienda: "IT98765432109",
  sdi_azienda: "ABC1234",
  email_azienda: "info@acmecorp.com",
  telefono_azienda: "+39 02 1234567",

  subtotale: "€ 10.000,00",
  sconto: "€ 500,00",
  inarcassa: "€ 380,00",
  iva: "€ 2.193,60",
  totale: "€ 12.073,60",

  ha_voci: true,

  voci: [
    {
      nome_voce: "Brand identity design",
      descrizione_voce: "Progettazione logo, palette colori e linee guida brand",
      categoria_voce: "Branding",
      prezzo_voce: "€ 5.000,00",
    },
    {
      nome_voce: "Web design e sviluppo",
      descrizione_voce: "Sito vetrina responsive, CMS custom, SEO base",
      categoria_voce: "Digital",
      prezzo_voce: "€ 5.000,00",
    },
  ],

  ha_voci_vacazione: true,

  voci_vacazione: [
    {
      descrizione_voce: "Consulenza strategica",
      ore: "20",
      prezzo_voce: "€ 3.000,00",
    },
    {
      descrizione_voce: "Art direction",
      ore: "15",
      prezzo_voce: "€ 2.000,00",
    },
  ],
};

/**
 * Mock data used to validate a custom contract template at upload time.
 * Covers all 3 payment modes (DATE, SAL, TASK) so the loop renders fully.
 */
export const CONTRACT_TEMPLATE_MOCK_DATA: ContractTemplateVariables = {
  codice_contratto: "CTR-2024-001",
  nome_contratto: "Contratto di prestazione servizi",
  data_firma: "10/04/2026",
  data_generazione: "15/04/2026",

  is_vacazione: true,

  nome_cliente: "Mario Rossi S.r.l.",
  piva_cliente: "IT12345678901",
  indirizzo_cliente: "Via Venezia 45, 20121 Milano (MI)",
  telefono_cliente: "+39 02 9876543",
  email_cliente: "mario.rossi@example.com",

  nome_azienda: "Acme Corporation S.r.l.",
  indirizzo_azienda: "Via Roma 123",
  citta_azienda: "Milano",
  provincia_azienda: "MI",
  cap_azienda: "20100",
  piva_azienda: "IT98765432109",
  email_azienda: "info@acmecorp.com",
  compagnia_assicurativa: "Allianz S.p.A.",
  numero_polizza: "POL-2024-001234",

  importo: "€ 10.000,00",
  sconto: "€ 500,00",
  totale: "€ 9.500,00",

  pagamenti: [
    {
      importo: "€ 3.000,00",
      modalita: "Data",
      scadenza: "30/04/2026",
      note: "Acconto alla firma",
    },
    {
      importo: "€ 4.000,00",
      modalita: "SAL",
      scadenza: "50%",
      note: "Pagamento al raggiungimento del 50% SAL",
    },
    {
      importo: "€ 2.500,00",
      modalita: "Task",
      scadenza: "Consegna finale",
      note: "Saldo al completamento del task",
    },
  ],
};

// ============================================================================
// Canonical placeholder catalog
// ============================================================================
// Used by:
//  - the validation result builder, to detect placeholders the user removed
//    from their template
//  - the "Elenco segnaposto" reference dialog, to render a structured list
//    grouped by category with Italian descriptions
// ============================================================================

/**
 * All quote placeholders the user can use, with Italian descriptions and
 * grouping for the reference dialog.
 *
 * The `key` is the canonical name without braces (`nome_cliente`, not
 * `{nome_cliente}`). Loop boundary keys are listed once with `isLoop: true`;
 * the keys *inside* a loop (e.g. `nome_voce`) are listed under their own
 * group with normal text-placeholder semantics.
 */
export const QUOTE_PLACEHOLDERS: TemplatePlaceholderMetadata[] = [
  // General
  {
    key: "codice_preventivo",
    description: "Codice del preventivo",
  },
  {
    key: "nome_preventivo",
    description: "Nome del preventivo",
  },
  {
    key: "data_generazione",
    description: "Data odierna in formato GG/MM/AAAA",
  },

  // Customer
  { key: "nome_cliente", description: "Nome del cliente" },
  {
    key: "piva_cliente",
    description: "P.IVA o codice fiscale del cliente",
  },

  // Tenant
  {
    key: "nome_azienda",
    description: "Nome della tua azienda",
  },
  {
    key: "indirizzo_azienda",
    description: "Via e numero civico",
  },
  { key: "citta_azienda", description: "Città" },
  { key: "cap_azienda", description: "CAP" },
  { key: "piva_azienda", description: "P.IVA" },
  {
    key: "provincia_azienda",
    description: "Sigla provincia (es. MI)",
  },
  {
    key: "sdi_azienda",
    description: "Codice destinatario SDI",
  },
  {
    key: "email_azienda",
    description: "Email aziendale",
  },
  {
    key: "telefono_azienda",
    description: "Telefono aziendale",
  },

  // Totals
  {
    key: "subtotale",
    description: "Importo subtotale (prima di sconto e IVA)",
  },
  {
    key: "sconto",
    description:
      "Sconto applicato — avvolgi la riga in {#sconto}...{/sconto} per nasconderla quando assente",
  },
  {
    key: "inarcassa",
    description:
      "Contributo Inarcassa (4%) — avvolgi la riga in {#inarcassa}...{/inarcassa} per nasconderla quando disabilitato",
  },
  {
    key: "iva",
    description: "IVA al 22%",
  },
  {
    key: "totale",
    description: "Totale finale",
  },

  // Price list conditional title + loop
  {
    key: "ha_voci",
    description:
      "Condizionale per il titolo della sezione voci. Avvolgi il titolo in {#ha_voci}...{/ha_voci} per nasconderlo quando non ci sono voci di listino.",
  },
  {
    key: "voci",
    description:
      "Inizio/fine ripetizione delle voci di listino. Usa {#voci} prima e {/voci} dopo.",
    isLoop: true,
  },
  {
    key: "nome_voce",
    description: "Nome della singola voce (dentro {#voci}...{/voci})",
  },
  {
    key: "descrizione_voce",
    description: "Descrizione estesa della voce",
  },
  {
    key: "categoria_voce",
    description: "Categoria della voce",
  },
  {
    key: "prezzo_voce",
    description: "Prezzo della voce in euro",
  },

  // Vacation items conditional title + loop
  {
    key: "ha_voci_vacazione",
    description:
      "Condizionale per il titolo della sezione vacazione. Avvolgi il titolo in {#ha_voci_vacazione}...{/ha_voci_vacazione} per nasconderlo quando non ci sono voci a vacazione.",
  },
  {
    key: "voci_vacazione",
    description:
      "Inizio/fine ripetizione delle voci a vacazione. Usa {#voci_vacazione} prima e {/voci_vacazione} dopo.",
    isLoop: true,
  },
  {
    key: "ore",
    description: "Numero di ore (dentro {#voci_vacazione}...{/voci_vacazione})",
  },
];

/**
 * All contract placeholders the user can use, same conventions as
 * {@link QUOTE_PLACEHOLDERS}.
 */
export const CONTRACT_PLACEHOLDERS: TemplatePlaceholderMetadata[] = [
  // General
  {
    key: "codice_contratto",
    description: "Codice del contratto",
  },
  {
    key: "nome_contratto",
    description: "Nome del contratto",
  },
  {
    key: "data_firma",
    description: "Data di firma in formato GG/MM/AAAA",
  },
  {
    key: "data_generazione",
    description: "Data di generazione del documento (oggi)",
  },
  {
    key: "is_vacazione",
    description:
      "Condizionale di modalità: vero per i contratti a vacazione. Avvolgi il " +
      "testo a vacazione in {#is_vacazione}...{/is_vacazione} e quello a corpo " +
      "nella sezione inversa {^is_vacazione}...{/is_vacazione} (Art. 5 e 6).",
  },

  // Customer
  { key: "nome_cliente", description: "Nome del cliente" },
  {
    key: "piva_cliente",
    description: "P.IVA o codice fiscale del cliente",
  },
  {
    key: "indirizzo_cliente",
    description: "Indirizzo completo del cliente",
  },
  {
    key: "telefono_cliente",
    description: "Telefono del cliente",
  },
  {
    key: "email_cliente",
    description: "Email del cliente",
  },

  // Tenant
  {
    key: "nome_azienda",
    description: "Nome della tua azienda",
  },
  {
    key: "indirizzo_azienda",
    description: "Via e numero civico",
  },
  { key: "citta_azienda", description: "Città" },
  { key: "cap_azienda", description: "CAP" },
  { key: "piva_azienda", description: "P.IVA" },
  {
    key: "provincia_azienda",
    description: "Sigla provincia (Foro competente, Art. 9)",
  },
  {
    key: "email_azienda",
    description: "Email aziendale",
  },
  {
    key: "compagnia_assicurativa",
    description: "Compagnia assicurativa (Art. 8)",
  },
  {
    key: "numero_polizza",
    description: "Numero polizza assicurativa (Art. 8)",
  },

  // Totals
  {
    key: "importo",
    description: "Importo del contratto (Art. 5)",
  },
  {
    key: "sconto",
    description:
      "Sconto applicato — avvolgi la riga in {#sconto}...{/sconto} per nasconderla quando assente",
  },
  {
    key: "totale",
    description: "Totale finale dopo lo sconto",
  },

  // Payments loop
  {
    key: "pagamenti",
    description:
      "Inizio/fine ripetizione dei pagamenti (Art. 6). Usa {#pagamenti} prima e {/pagamenti} dopo.",
    isLoop: true,
  },
  {
    key: "modalita",
    description:
      "Modalità di pagamento (Data, SAL, Task) — dentro {#pagamenti}...{/pagamenti}",
  },
  {
    key: "scadenza",
    description:
      "Scadenza o riferimento (data, percentuale SAL, nome task)",
  },
  {
    key: "note",
    description: "Note aggiuntive del pagamento",
  },
];
